import { ReactElement, ReactNode } from 'react';
import { FormInlineError } from 'components';
import { AttachmentField } from 'modules/attachments/AttachmentField';
import { TextField, DateRangePickerField, SelectField, useField } from '@ff-it/form';
import { agreementKindOptions, regularityOptions } from 'configuration';
import { Perm, useHasPerm } from 'core/permissions';
import { CompanyField } from 'modules/core/company/components';
import type { Bonus } from 'modules/guarantee/types';
import { fmtMonth } from 'utilities';

interface BonusFormProps {
  children?: ReactNode;
  sensitive: Perm;
}

export function BonusForm({ sensitive, children }: BonusFormProps): ReactElement {
  const canViewSensitive = useHasPerm(sensitive);
  const {
    input: { value: finance_period },
  } = useField<Bonus['finance_period']>('finance_period', { subscription: { value: true } });
  const periodHelp = finance_period ? `${fmtMonth(finance_period.start)} - ${fmtMonth(finance_period.end)}` : undefined;
  return (
    <div className="container px-0 mb-5 ml-0">
      <div className="form-row">
        <TextField name="title" label="Title" className="col-md-3" required />
        <CompanyField
          name="partner"
          label="Partner"
          className="col-md-3"
          required
          help="Payer"
          includeBranches={true}
        />
        <TextField name="number" label="Number" className="col-md-3" help="Leave blank to assign" />
        <DateRangePickerField
          label="Period"
          fromName="date_from"
          toName="date_to"
          className="col-md-3"
          required
          help={periodHelp}
        />
      </div>
      <div className="form-row">
        <SelectField
          label="Regularity"
          name="regularity"
          options={regularityOptions}
          simple
          required
          className="col-md-3"
        />
        <SelectField label="Kind" name="kind" options={agreementKindOptions} simple required className="col-md-3" />
        {canViewSensitive && <AttachmentField name="attachments" label="Attachments" className="col-md-6" />}
      </div>
      {canViewSensitive && (
        <div className="form-row">
          <TextField
            name="terms"
            className="col-6"
            label="Terms and conditions"
            help="General terms and conditions"
            multiline
            rows={2}
          />
          <TextField
            name="notes"
            className="col-6"
            label="Notes"
            help="Private notes for internal use"
            multiline
            rows={2}
          />
        </div>
      )}

      <hr />
      <FormInlineError name="rates" />
      {children}
    </div>
  );
}
